import {qtyNum} from './format';
import {STAMM_LABELS, availCat, groupLabelOf, isExcluded, stammLagerOf} from './availability';

// Ansichtslogik der Positionstabelle (Original `viewIndices`, Zeile 1432).
// Sie ist der einzige Ort, an dem Filter wirken — die Arbeitsliste behält immer
// die Reihenfolge des Belegs, weil gespeichert wird, was sich gegenüber dem
// geladenen Stand geändert hat.

/** Liegt ein Wert im angegebenen Bereich? Leere Grenze heißt „offen". */
function inRange(v, mn, mx) {
    const lo = mn === '' || v >= parseFloat(String(mn).replace(',', '.'));
    const hi = mx === '' || v <= parseFloat(String(mx).replace(',', '.'));
    return lo && hi;
}

/**
 * Filter-Kategorie der Verfügbarkeitsknöpfe: wie availCat, aber Positionen
 * auf „Keine" bekommen den eigenen Wert 'none' (Ticket #129). Nur für die
 * Anzeige — availCat selbst bleibt unverändert.
 */
export function filterCat(r, stock, order) {
    return isExcluded(r) ? 'none' : availCat(r, stock, order);
}

/**
 * Sichtbare Positionen nach allen Filtern — in der Reihenfolge des Belegs.
 * Sortiert wird danach von der Tabelle selbst.
 *
 * @param {{row: object, index: number, pos: string}[]} entries
 */
export function filterEntries(entries, view, stock, order, mode) {
    let out = entries;

    if (view.availFilter.length) {
        const set = new Set(view.availFilter);
        out = out.filter((e) => set.has(filterCat(e.row, stock, order)));
    }
    if (view.mbFilter) {
        out = out.filter((e) => (stock.masterbox[String(e.row.articleId)] || 0) > 0);
    }
    if (view.posSearch) {
        out = out.filter((e) => String(e.pos || (e.index + 1)).startsWith(view.posSearch));
    }
    if (view.artSearch) {
        const q = view.artSearch.toLowerCase();
        out = out.filter((e) => (e.row.articleNumber || '').toLowerCase().includes(q));
    }
    if (view.nameSearch) {
        const q = view.nameSearch.toLowerCase();
        out = out.filter((e) => (((e.row.title || '') + ' ' + (e.row.articleNumber || '')).toLowerCase()).includes(q));
    }
    if (view.qtyMin !== '' || view.qtyMax !== '') {
        out = out.filter((e) => inRange(qtyNum(e.row.quantity), view.qtyMin, view.qtyMax));
    }
    if (view.discMin !== '' || view.discMax !== '') {
        out = out.filter((e) => inRange(parseFloat(e.row.discountPercentage) || 0, view.discMin, view.discMax));
    }
    if (view.groupFilter !== 'all') {
        out = out.filter((e) => ((stock.groups && stock.groups[String(e.row.articleId)]) || 'none') === view.groupFilter);
    }
    if (view.stammFilter !== 'all' && mode === 'order') {
        out = out.filter((e) => stammLagerOf(e.row, stock) === view.stammFilter);
    }

    return out;
}

/**
 * Sortierwert je Schlüssel — exakt die acht Schlüssel des Originals.
 * `free` hat seit dem Entfall der Spalte „Frei" keinen Spaltenkopf mehr; der
 * Schlüssel bleibt erhalten, damit die Logik vollständig ist.
 */
export function sortValue(key, entry, stock, order) {
    const r = entry.row;
    switch (key) {
        case 'art':
            return r.articleNumber || '';
        case 'name':
            return r.title || '';
        case 'group':
            // Positionen ohne Gruppe ans Ende.
            return groupLabelOf(r, stock) || '￿';
        case 'stamm':
            return stammLagerOf(r, stock);
        case 'qty':
            return parseFloat(r.quantity) || 0;
        case 'price':
            return parseFloat(r.unitPrice) || 0;
        case 'avail':
            return availCat(r, stock, order);
        default:
            return entry.index;
    }
}

/** Vergleich wie im Original: kleiner/größer ohne Sprachregeln. */
export function compareValues(a, b) {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}

/** Aktive Filter als Chips für die Filter-Pille (Original `updateFilterCounts`). */
export function activeFilterChips(view, mode) {
    const chips = [];
    if (view.availFilter.length) {
        chips.push(`Verfügbarkeit (${view.availFilter.length})`);
    }
    if (view.posSearch) {
        chips.push(`Pos ${view.posSearch}`);
    }
    if (view.artSearch) {
        chips.push(`Art-Nr „${view.artSearch}"`);
    }
    if (view.nameSearch) {
        chips.push(`Suche „${view.nameSearch}"`);
    }
    if (view.mbFilter) {
        chips.push('nur Masterbox');
    }
    if (view.groupFilter !== 'all') {
        chips.push(`Rabattgruppe ${view.groupFilter}`);
    }
    if (view.qtyMin !== '' || view.qtyMax !== '') {
        chips.push(`Menge ${view.qtyMin || '0'}–${view.qtyMax || '∞'}`);
    }
    if (view.discMin !== '' || view.discMax !== '') {
        chips.push(`Rabatt ${view.discMin || '0'}–${view.discMax || '∞'} %`);
    }
    if (mode === 'order' && view.stammFilter !== 'all') {
        chips.push(`Stammlager ${STAMM_LABELS[view.stammFilter] || view.stammFilter}`);
    }
    return chips;
}
